import React, { useState, useEffect } from 'react';
import { api } from '@/api/apiClient';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import FlexCalculator from '@/components/enrollment/FlexCalculator';
import { Save, CheckCircle2, AlertCircle, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

export default function FlexOptimization() {
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [optimalResult, setOptimalResult] = useState(null);
  const [missingData, setMissingData] = useState([]);

  useEffect(() => {
    loadEmployee();
  }, []);

  const loadEmployee = async () => {
    try {
      const user = await api.auth.me();
      const employees = await api.entities.Employee.filter({ user_email: user.email });
      
      if (employees.length > 0) {
        const emp = employees[0];
        setEmployee(emp);
        
        // Check for missing critical data
        const missing = [];
        if (!emp.annual_salary || emp.annual_salary <= 0) {
          missing.push('Annual Salary');
        }
        if (!emp.filing_status) {
          missing.push('Filing Status');
        }
        setMissingData(missing);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveOptimal = async () => {
    if (!optimalResult || !employee) return;
    
    setSaving(true);
    try {
      // Ensure we save as numbers
      const optimalAmount = Number(optimalResult.optimalFlexAmount);
      
      await api.entities.Employee.update(employee.id, {
        optimal_flex_amount: optimalAmount,
        flex_allocation: optimalAmount
      });
      
      toast.success(
        <div>
          <p className="font-semibold">✓ Optimal premium amount saved!</p>
          <p className="text-sm">This will be pre-filled during your benefit enrollment.</p>
        </div>,
        { duration: 4000 }
      );
      
      await loadEmployee();
      
      // Scroll to top to show the saved amount badge
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error(err);
      toast.error('Failed to save premium amount');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!employee) {
    return (
      <Alert>
        <AlertDescription>
          Employee profile not found. Please contact your administrator.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Premium Optimization</h1>
          <p className="text-slate-500 mt-1">
            Find the optimal Section 125 contribution to maximize your tax savings
          </p>
        </div>

        {employee.optimal_flex_amount && Number(employee.optimal_flex_amount) > 0 && (
          <Card className="bg-emerald-50 border-emerald-200 self-start sm:self-auto">
            <CardContent className="p-4 flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              <div>
                <p className="text-sm text-emerald-600">Saved Optimal Amount</p>
                <p className="text-lg font-bold text-emerald-900">
                  ${Number(employee.optimal_flex_amount).toFixed(2)}/mo
                </p>
                <p className="text-xs text-emerald-600 mt-1">
                  Used during enrollment
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* What Is This? Explanation Card */}
      <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-blue-900">
            <TrendingUp className="w-5 h-5" />
            What is Premium Optimization?
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-blue-900">
          <p className="font-medium">
            This calculator helps you determine the optimal amount of pre-tax dollars to allocate 
            toward your benefits through a Section 125 Cafeteria Plan.
          </p>
          
          <div className="bg-white/70 rounded-lg p-3 mt-3 border border-blue-300">
            <p className="font-semibold text-sm mb-1">💾 Why Save Your Optimal Amount?</p>
            <p className="text-sm">
              Saving this amount will <strong>automatically pre-fill</strong> your premium allocation
              during benefit enrollment, making the signup process faster and ensuring you get
              the maximum tax savings.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4 mt-4">
            <div className="bg-white/70 rounded-lg p-4">
              <h4 className="font-semibold mb-2">💰 How You Save Money</h4>
              <ul className="text-sm space-y-1 list-none">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-600 mt-0.5">✓</span>
                  <span>Reduces your taxable income</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-600 mt-0.5">✓</span>
                  <span>Saves on federal income tax</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-600 mt-0.5">✓</span>
                  <span>Saves on FICA (Social Security + Medicare)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-600 mt-0.5">✓</span>
                  <span>Typical savings: 25-35% of premium amount</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-white/70 rounded-lg p-4">
              <h4 className="font-semibold mb-2">🎯 How It's Used</h4>
              <ul className="text-sm space-y-1 list-none">
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-0.5">→</span>
                  <span>Pays for insurance premiums pre-tax</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-0.5">→</span>
                  <span>Unused funds = 86% reimbursement</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-0.5">→</span>
                  <span>Applied to qualified medical expenses</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-0.5">→</span>
                  <span>Maximizes your take-home benefit</span>
                </li>
              </ul>
            </div>
          </div>
          
          <p className="text-sm italic mt-3">
            <strong>Example:</strong> If you allocate $2,000/month to premium dollars, you might save
            $500-$700/month in taxes, giving you more spending power for your benefits!
          </p>
        </CardContent>
      </Card>

      {/* Missing Data Warning */}
      {missingData.length > 0 && (
        <Alert className="bg-amber-50 border-amber-200">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-900">
            <strong>Missing Information:</strong> {missingData.join(', ')} not found in your profile. 
            The calculator will use default values, but results may not be accurate. 
            Please update your demographics for precise calculations.
          </AlertDescription>
        </Alert>
      )}
      
      {/* Demographics Summary */}
      {(employee.annual_salary > 0 || employee.filing_status) && (
        <Card className="bg-slate-50 border-slate-200">
          <CardContent className="p-4">
            <p className="text-sm text-slate-600 mb-2">
              <strong>Your Demographics (auto-loaded):</strong>
            </p>
            <div className="flex gap-6 text-sm">
              {employee.annual_salary > 0 && (
                <div>
                  <span className="text-slate-500">Annual Salary:</span>{' '}
                  <strong className="text-slate-900">
                    ${employee.annual_salary.toLocaleString()}
                  </strong>
                </div>
              )}
              {employee.filing_status && (
                <div>
                  <span className="text-slate-500">Filing Status:</span>{' '}
                  <strong className="text-slate-900 capitalize">
                    {employee.filing_status.replace('_', ' ')}
                  </strong>
                </div>
              )}
              {employee.dependents > 0 && (
                <div>
                  <span className="text-slate-500">Dependents:</span>{' '}
                  <strong className="text-slate-900">{employee.dependents}</strong>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <FlexCalculator 
        employee={employee}
        onOptimalCalculated={setOptimalResult}
        onAutoSaved={loadEmployee}
      />

      {optimalResult && (
        <div className="flex justify-end gap-4">
          <Button
            onClick={handleSaveOptimal}
            disabled={saving}
            className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
          >
            {saving ? (
              <>Saving...</>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Optimal Amount to Profile
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}